// src/components/FieldConfig.jsx
import React, { useState } from 'react';
import {
    Paper,
    TextField,
    Box,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    IconButton,
    Button,
    Chip,
    Divider,
    Card,
    CardContent,
    Tooltip,
    Collapse,
    alpha,
    Badge,
    FormHelperText,
    Alert
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import NotesIcon from '@mui/icons-material/Notes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AlertNotesConfig from './AlertNotesConfig';
import { InputTypes, DataTypes, US_STATES, TIMEZONES, HOURS, MINUTES, AMPM_OPTIONS } from '../types/schema';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { SERVER_VALIDATION_TYPES } from '../types/schema';

// Import @dnd-kit
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimeFieldConfig from './TimeFieldConfig';
import ConditionalLogicConfig from './ConditionalLogicConfig';

// Field type icon mapper (unchanged)
const getFieldTypeIcon = (type) => {
    switch (type) {
        case InputTypes.CHECKBOX:
            return <CheckBoxIcon />;
        case InputTypes.TEXTBOX:
            return <TextFieldsIcon />;
        case InputTypes.DROPDOWN:
            return <ArrowDropDownIcon />;
        case InputTypes.RADIO:
            return <RadioButtonCheckedIcon />;
        case InputTypes.TEXTAREA:
            return <NotesIcon />;
        case InputTypes.DATEPICKER:
            return <CalendarTodayIcon />;
        default:
            return <TextFieldsIcon />;
    }
};

// Validation status component (unchanged)
const ValidationStatus = ({ field }) => {
    const hasValidation = field.validation.required ||
        field.validation.minLength ||
        field.validation.maxLength ||
        field.validation.minValue ||
        field.validation.maxValue;

    const isComplete = field.name && field.label;

    return (
        <Box display="flex" gap={1} alignItems="center">
            {isComplete ? (
                <Tooltip title="Field configuration complete" arrow>
                    <Chip
                        icon={<CheckCircleIcon />}
                        label="Complete"
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                </Tooltip>
            ) : (
                <Tooltip title="Required fields missing" arrow>
                    <Chip
                        icon={<ErrorOutlineIcon />}
                        label="Incomplete"
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                </Tooltip>
            )}
            {hasValidation && (
                <Tooltip title="Has validation rules" arrow>
                    <Chip
                        icon={<SettingsIcon />}
                        label="Validated"
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </Tooltip>
            )}
        </Box>
    );
};

// Option card component (unchanged)
const OptionCard = ({ option, index, onChange, onDelete }) => (
    <Card
        elevation={0}
        sx={{
            border: 1,
            borderColor: 'divider',
            transition: 'all 0.2s',
            '&:hover': {
                borderColor: 'primary.main',
                boxShadow: 2
            }
        }}
    >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" alignItems="center" gap={1}>
                <Badge badgeContent={index + 1} color="primary" sx={{ mr: 1 }}>
                    <Box width={20} />
                </Badge>
                <TextField
                    fullWidth
                    size="small"
                    value={option}
                    onChange={(e) => onChange(index, e.target.value)}
                    placeholder={`Enter option ${index + 1}`}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                                borderColor: 'primary.main',
                            }
                        }
                    }}
                />
                <Tooltip title="Delete option" arrow>
                    <IconButton
                        onClick={() => onDelete(index)}
                        size="small"
                        color="error"
                        sx={{
                            '&:hover': {
                                bgcolor: alpha('#d32f2f', 0.1)
                            }
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </CardContent>
    </Card>
);

const FieldConfig = ({
    field,
    index,
    onChange,
    onDelete,
    totalFields,
    availableFieldsInCurrentSection = [],
    currentSectionId = ''
}) => {
    const [showOptions, setShowOptions] = useState(
        field.type === InputTypes.DROPDOWN || field.type === InputTypes.RADIO || field.type === InputTypes.CHECKBOX || field.type === InputTypes.STATE
    );
    const [validationExpanded, setValidationExpanded] = useState(true);
    const [optionsExpanded, setOptionsExpanded] = useState(false);
    const [alertsExpanded, setAlertsExpanded] = useState(false);
    const [conditionalExpanded, setConditionalExpanded] = useState(false);
    const [serverValidationsExpanded, setServerValidationsExpanded] = useState(false);


    const [timeConfigExpanded, setTimeConfigExpanded] = useState(false);

    // Use sortable hook from @dnd-kit
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: field.id || `field-${index}`,
        data: { index, field }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative'
    };

    const handleAddServerValidation = () => {
        const newValidation = {
            id: `sv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type: SERVER_VALIDATION_TYPES.CLIENT_ACCOUNT_NUMBER_EXISTS,
            message: '',
            enabled: true
        };

        onChange({
            ...field,
            serverValidations: [...(field.serverValidations || []), newValidation]
        });
    };

    const handleServerValidationChange = (index, property, value) => {
        const newValidations = [...(field.serverValidations || [])];
        newValidations[index] = { ...newValidations[index], [property]: value };
        onChange({ ...field, serverValidations: newValidations });
    };

    const handleDeleteServerValidation = (index) => {
        const newValidations = field.serverValidations?.filter((_, i) => i !== index) || [];
        onChange({ ...field, serverValidations: newValidations });
    };


    const handleFieldChange = (property) => (event) => {
        const value = event.target.value;

        if (property === 'type') {
            const updatedField = { ...field, type: value };

            // Handle special configurations for different types
            if (value === InputTypes.DROPDOWN || value === InputTypes.RADIO || value === InputTypes.CHECKBOX) {
                updatedField.options = field.options && field.options.length > 0
                    ? field.options
                    : ['Option 1', 'Option 2'];

                // For checkbox, just set it as a checkbox group (multiple selection)
                if (value === InputTypes.CHECKBOX) {
                    updatedField.checkboxConfig = {
                        isSingle: false,
                        isMultiple: true,
                        defaultChecked: []
                    };
                } else if (updatedField.checkboxConfig) {
                    delete updatedField.checkboxConfig;
                }
            } else if (value === InputTypes.STATE) {
                updatedField.options = US_STATES;
                updatedField.placeholder = '';
            } else if (value === InputTypes.EMAIL) {
                updatedField.validation = {
                    ...field.validation,
                    emailValidation: true,
                    dataType: DataTypes.EMAIL
                };
                updatedField.placeholder = 'example@domain.com';
            } else if (value === InputTypes.PHONE) {
                updatedField.validation = {
                    ...field.validation,
                    phoneValidation: true,
                    dataType: DataTypes.PHONE
                };
                updatedField.placeholder = '(123) 456-7890';
            } else if (value === InputTypes.TIME) {
                updatedField.timeConfig = {
                    includeTimezone: true,
                    availableTimezones: TIMEZONES,
                    hourOptions: HOURS,
                    minuteOptions: MINUTES,
                    ampmOptions: AMPM_OPTIONS
                };
                updatedField.placeholder = '';
                updatedField.validation.timezone = 'UTC';
            } else {
                updatedField.options = [];
                if (updatedField.timeConfig) delete updatedField.timeConfig;
            }

            onChange(updatedField);
            setShowOptions(value === InputTypes.DROPDOWN ||
                value === InputTypes.CHECKBOX ||
                value === InputTypes.RADIO ||
                value === InputTypes.STATE);
            setTimeConfigExpanded(value === InputTypes.TIME);
        } else if (property === 'dataType') {
            onChange({
                ...field,
                validation: {
                    ...field.validation,
                    dataType: value
                }
            });
        } else {
            onChange({
                ...field,
                [property]: value
            });
        }
    };

    const handleValidationChange = (property) => (event) => {
        const value = event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value;

        // Handle minSelections and maxSelections (checkbox-specific)
        if (property === 'minSelections' || property === 'maxSelections') {
            onChange({
                ...field,
                validation: {
                    ...field.validation,
                    [property]: value === '' ? null : Number(value)
                }
            });
        } else {
            onChange({
                ...field,
                validation: {
                    ...field.validation,
                    [property]: property === 'required' ? value : (value === '' ? null : Number(value))
                }
            });
        }
    };

    const handleOptionsChange = (index, value) => {
        const newOptions = [...field.options];
        newOptions[index] = value;
        onChange({ ...field, options: newOptions });
    };

    const handleAddOption = () => {
        onChange({
            ...field,
            options: [...field.options, `Option ${field.options.length + 1}`]
        });
    };

    const handleDeleteOption = (index) => {
        const newOptions = field.options.filter((_, i) => i !== index);
        onChange({ ...field, options: newOptions });
    };

    const handleAlertNotesChange = (notes) => {
        onChange({ ...field, alertNotes: notes });
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            elevation={isDragging ? 6 : 3}
            sx={{
                p: 3,
                mb: 3,
                position: 'relative',
                borderRadius: 2,
                border: 2,
                borderColor: isDragging ? 'primary.main' : 'divider',
                borderStyle: isDragging ? 'dashed' : 'solid',
                transition: 'all 0.3s',
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 6
                },
                backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                cursor: isDragging ? 'grabbing' : 'grab'
            }}
        >
            {/* Header with drag handle */}
            <Box
                display="flex"
                alignItems="center"
                mb={3}
                pb={2}
                borderBottom={2}
                borderColor="primary.main"
            >
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{
                        mr: 1,
                        cursor: isDragging ? 'grabbing' : 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        color: isDragging ? 'primary.main' : 'action.active',
                        '&:hover': {
                            color: 'primary.main'
                        }
                    }}
                >
                    <DragIndicatorIcon />
                </Box>
                <Box
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                        mr: 1
                    }}
                >
                    <Typography variant="subtitle2" fontWeight={600}>
                        #{index + 1}
                    </Typography>
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor: alpha('#1976d2', 0.1),
                        borderRadius: 1
                    }}
                >
                    {getFieldTypeIcon(field.type)}
                    <Typography variant="subtitle1" fontWeight={600}>
                        {field.label || field.name || `Field ${index + 1}`}
                    </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <ValidationStatus field={field} />
                <Tooltip title="Delete field" arrow>
                    <IconButton
                        onClick={onDelete}
                        color="error"
                        sx={{
                            ml: 1,
                            '&:hover': {
                                bgcolor: alpha('#d32f2f', 0.1)
                            }
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box
                mb={3}
                p={2}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: 'background.paper'
                }}
            >
                {/* Section Header */}
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={2}
                >
                    <TextFieldsIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                        Basic Configuration
                    </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    {/* Field Type */}
                    <Grid item>
                        <FormControl fullWidth size="small">
                            <InputLabel>Field Type *</InputLabel>
                            <Select
                                value={field.type}
                                onChange={handleFieldChange('type')}
                                label="Field Type *"
                            >
                                <MenuItem value={InputTypes.TEXTBOX}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <TextFieldsIcon fontSize="small" />
                                        Text Box
                                    </Box>
                                </MenuItem>

                                <MenuItem value={InputTypes.DROPDOWN}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <ArrowDropDownIcon fontSize="small" />
                                        Dropdown
                                    </Box>
                                </MenuItem>

                                <MenuItem value={InputTypes.RADIO}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <RadioButtonCheckedIcon fontSize="small" />
                                        Radio Buttons
                                    </Box>
                                </MenuItem>

                                <MenuItem value={InputTypes.CHECKBOX}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CheckBoxIcon fontSize="small" />
                                        Checkbox / Checkbox Group
                                    </Box>
                                </MenuItem>

                                <MenuItem value={InputTypes.TEXTAREA}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <NotesIcon fontSize="small" />
                                        Text Area
                                    </Box>
                                </MenuItem>

                                <MenuItem value={InputTypes.DATEPICKER}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CalendarTodayIcon fontSize="small" />
                                        Date Picker
                                    </Box>
                                </MenuItem>

                                <MenuItem value={InputTypes.EMAIL}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <EmailIcon fontSize="small" />
                                        Email (Pre-built)
                                    </Box>
                                </MenuItem>

                                <MenuItem value={InputTypes.PHONE}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <PhoneIcon fontSize="small" />
                                        Phone (US Format)
                                    </Box>
                                </MenuItem>

                                <MenuItem value={InputTypes.STATE}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <LocationOnIcon fontSize="small" />
                                        State (US States)
                                    </Box>
                                </MenuItem>

                                <MenuItem value={InputTypes.TIME}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <AccessTimeIcon fontSize="small" />
                                        Time (with Timezone)
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Field Name */}
                    <Grid>
                        <TextField
                            fullWidth
                            size="small"
                            label="Field Name"
                            value={field.name}
                            onChange={handleFieldChange('name')}
                            required
                            error={!field.name}
                            helperText="Internal key (no spaces)"
                        />
                    </Grid>

                    {/* Field Label */}
                    <Grid>
                        <TextField
                            fullWidth
                            size="small"
                            label="Field Label"
                            value={field.label}
                            onChange={handleFieldChange('label')}
                            required
                            error={!field.label}
                            helperText="Visible to end users"
                        />
                    </Grid>

                    {/* Description */}
                    <Grid>
                        <TextField
                            fullWidth
                            size="small"
                            label="Description"
                            value={field.description}
                            onChange={handleFieldChange('description')}
                            multiline
                            rows={2}
                            placeholder="Optional helper text shown below the field"
                        />
                    </Grid>

                    {/* Placeholder */}
                    <Grid item xs={12} md={field.type === InputTypes.TEXTBOX ? 8 : 12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Placeholder"
                            value={field.placeholder}
                            onChange={handleFieldChange('placeholder')}
                            placeholder="Example input shown to users"
                        />
                    </Grid>

                    {/* Data Type (Conditional) */}
                    {field.type === InputTypes.TEXTBOX && (
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Data Type</InputLabel>
                                <Select
                                    value={field.validation.dataType}
                                    onChange={handleFieldChange('dataType')}
                                    label="Data Type"
                                >
                                    <MenuItem value={DataTypes.TEXT}>Text</MenuItem>
                                    <MenuItem value={DataTypes.NUMERIC}>Numeric</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </Box>


            <Divider sx={{ my: 3 }} />

            {/* Validation Rules */}
            <Box mb={3}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: 'pointer', mb: 2 }}
                    onClick={() => setValidationExpanded(!validationExpanded)}
                >
                    <Typography
                        variant="h6"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ color: 'secondary.main' }}
                    >
                        <SettingsIcon /> Validation Rules
                    </Typography>
                    <IconButton size="small">
                        {validationExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={validationExpanded}>
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: alpha('#9c27b0', 0.05),
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider'
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={field.validation.required}
                                            onChange={handleValidationChange('required')}
                                            color="secondary"
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            Required Field
                                            {field.validation.required && (
                                                <Chip label="*" size="small" color="error" />
                                            )}
                                        </Box>
                                    }
                                />
                            </Grid>

                            {field.type !== InputTypes.DATEPICKER && (
                                <>
                                    <Grid item xs={12} md={field.validation.dataType === DataTypes.NUMERIC ? 3 : 4.5}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Min Length"
                                            type="number"
                                            value={field.validation.minLength || ''}
                                            onChange={handleValidationChange('minLength')}
                                            disabled={field.validation.dataType === DataTypes.NUMERIC}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={field.validation.dataType === DataTypes.NUMERIC ? 3 : 4.5}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Max Length"
                                            type="number"
                                            value={field.validation.maxLength || ''}
                                            onChange={handleValidationChange('maxLength')}
                                            disabled={field.validation.dataType === DataTypes.NUMERIC}
                                        />
                                    </Grid>
                                </>
                            )}

                            {field.validation.dataType === DataTypes.NUMERIC && field.type === InputTypes.TEXTBOX && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Min Value"
                                            type="number"
                                            value={field.validation.minValue || ''}
                                            onChange={handleValidationChange('minValue')}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Max Value"
                                            type="number"
                                            value={field.validation.maxValue || ''}
                                            onChange={handleValidationChange('maxValue')}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Box>
                </Collapse>
            </Box>

            <Divider sx={{ my: 3 }} />
            <Box>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: 'pointer', mb: 2 }}
                    onClick={() => setServerValidationsExpanded(!serverValidationsExpanded)}
                >
                    <Typography
                        variant="h6"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ color: 'warning.main' }}
                    >
                        <CloudUploadIcon /> Server-Side Validations
                        {field.serverValidations && field.serverValidations.length > 0 && (
                            <Chip
                                label={`${field.serverValidations.length} rule(s)`}
                                size="small"
                                color="warning"
                                variant="outlined"
                            />
                        )}
                    </Typography>
                    <IconButton size="small">
                        {serverValidationsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={serverValidationsExpanded}>
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: alpha('#ed6c02', 0.05),
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider'
                        }}
                    >
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="caption">
                                These checks will verify data against our system records in real-time.
                            </Typography>
                        </Alert>

                        {field.serverValidations?.map((validation, index) => (
                            <Paper
                                key={validation.id}
                                elevation={1}
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    borderLeft: 4,
                                    borderColor: 'warning.main',
                                    bgcolor: alpha('#ed6c02', 0.05)
                                }}
                            >
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Chip label="Server-side" size="small" color="warning" />
                                        <Typography variant="subtitle2">
                                            Server Validation {index + 1}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteServerValidation(index)}
                                        color="error"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Validation Type</InputLabel>
                                            <Select
                                                value={validation.type || SERVER_VALIDATION_TYPES.CLIENT_ACCOUNT_NUMBER_EXISTS}
                                                onChange={(e) => handleServerValidationChange(index, 'type', e.target.value)}
                                                label="Validation Type"
                                            >
                                                <MenuItem value={SERVER_VALIDATION_TYPES.CLIENT_ACCOUNT_NUMBER_EXISTS}>
                                                    Client Account Number Exists
                                                </MenuItem>
                                                <MenuItem value={SERVER_VALIDATION_TYPES.LS_ACCOUNT_NUMBER_EXISTS}>
                                                    LS Account Number Exists
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Error Message"
                                            value={validation.message || ''}
                                            onChange={(e) => handleServerValidationChange(index, 'message', e.target.value)}
                                            placeholder="e.g., Account ID already exists"
                                            helperText="Leave empty for default message"
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}

                        <Button
                            startIcon={<AddIcon />}
                            onClick={handleAddServerValidation}
                            variant="outlined"
                            color="warning"
                            fullWidth
                            sx={{ mt: 2 }}
                        >
                            Add Server Validation
                        </Button>
                    </Box>
                </Collapse>
            </Box>

            {/* Options Section */}
            {showOptions && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Box mb={3}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ cursor: 'pointer', mb: 2 }}
                            onClick={() => setOptionsExpanded(!optionsExpanded)}
                        >
                            <Typography
                                variant="h6"
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ color: 'success.main' }}
                            >
                                <ListAltIcon /> Options
                                <Chip
                                    label={`${field.options.length} items`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            </Typography>
                            <IconButton size="small">
                                {optionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>

                        <Collapse in={optionsExpanded}>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: alpha('#2e7d32', 0.05),
                                    borderRadius: 2,
                                    border: 1,
                                    borderColor: 'divider'
                                }}
                            >
                                <Grid container spacing={2}>
                                    {field.options.map((option, optIndex) => (
                                        <Grid item xs={12} sm={6} md={4} key={optIndex}>
                                            <OptionCard
                                                option={option}
                                                index={optIndex}
                                                onChange={handleOptionsChange}
                                                onDelete={handleDeleteOption}
                                            />
                                        </Grid>
                                    ))}
                                    <Grid item xs={12}>
                                        <Button
                                            startIcon={<AddIcon />}
                                            onClick={handleAddOption}
                                            variant="outlined"
                                            color="success"
                                            fullWidth
                                            sx={{
                                                py: 1.5,
                                                borderStyle: 'dashed',
                                                '&:hover': {
                                                    borderStyle: 'solid'
                                                }
                                            }}
                                        >
                                            Add Option
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Collapse>
                    </Box>
                </>
            )}

            {/* Checkbox Configuration (Simplified) */}
            {field.type === InputTypes.CHECKBOX && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Box mb={3}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 2 }}
                        >
                            <Typography
                                variant="h6"
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ color: 'primary.main' }}
                            >
                                <CheckBoxIcon /> Checkbox Configuration
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                                bgcolor: alpha('#1976d2', 0.05),
                                borderRadius: 2,
                                border: 1,
                                borderColor: 'divider'
                            }}
                        >
                            <Grid container spacing={2} alignItems="center">
                                {/* Single vs Multiple Toggle */}
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!field.checkboxConfig?.isSingle}
                                                onChange={(e) => {
                                                    const isMultiple = e.target.checked;
                                                    const newConfig = {
                                                        ...field.checkboxConfig,
                                                        isSingle: !isMultiple,
                                                        isMultiple: isMultiple,
                                                        defaultChecked: isMultiple ? [] : false
                                                    };
                                                    onChange({
                                                        ...field,
                                                        checkboxConfig: newConfig
                                                    });
                                                }}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {field.checkboxConfig?.isSingle ? (
                                                    <>
                                                        <Typography>Single Checkbox</Typography>
                                                        <Chip label="True/False" size="small" color="info" variant="outlined" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <Typography>Checkbox Group</Typography>
                                                        <Chip label="Multiple Selection" size="small" color="success" variant="outlined" />
                                                    </>
                                                )}
                                            </Box>
                                        }
                                    />
                                    <FormHelperText>
                                        {field.checkboxConfig?.isSingle
                                            ? "Users can check/uncheck a single option (like a toggle)"
                                            : "Users can select multiple options from the list (like radio but with multiple selection)"
                                        }
                                    </FormHelperText>
                                </Grid>

                                {/* For single checkbox, show default state */}
                                {field.checkboxConfig?.isSingle && (
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={field.checkboxConfig?.defaultChecked || false}
                                                    onChange={(e) => {
                                                        onChange({
                                                            ...field,
                                                            checkboxConfig: {
                                                                ...field.checkboxConfig,
                                                                defaultChecked: e.target.checked
                                                            }
                                                        });
                                                    }}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    Default Checked
                                                    {field.checkboxConfig?.defaultChecked && (
                                                        <Chip label="Checked by default" size="small" color="success" variant="outlined" />
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </Grid>
                                )}

                                {/* For checkbox group, show validation */}
                                {!field.checkboxConfig?.isSingle && (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Minimum Selections"
                                                type="number"
                                                value={field.validation.minSelections || ''}
                                                onChange={handleValidationChange('minSelections')}
                                                helperText="Minimum options to select"
                                                InputProps={{ inputProps: { min: 0, max: field.options.length } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Maximum Selections"
                                                type="number"
                                                value={field.validation.maxSelections || ''}
                                                onChange={handleValidationChange('maxSelections')}
                                                helperText="Maximum options to select"
                                                InputProps={{ inputProps: { min: 0, max: field.options.length } }}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Box>
                    </Box>
                </>
            )}

            {/* Time Field Configuration */}
            {field.type === InputTypes.TIME && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Box mb={3}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ cursor: 'pointer', mb: 2 }}
                            onClick={() => setTimeConfigExpanded(!timeConfigExpanded)}
                        >
                            <Typography
                                variant="h6"
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ color: 'info.main' }}
                            >
                                <AccessTimeIcon /> Time Configuration
                            </Typography>
                            <IconButton size="small">
                                {timeConfigExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>

                        <Collapse in={timeConfigExpanded}>
                            <TimeFieldConfig
                                field={field}
                                onChange={onChange}
                            />
                        </Collapse>
                    </Box>
                </>
            )}

            {/* Alert Notes */}
            <Divider sx={{ my: 3 }} />
            <Box>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: 'pointer', mb: 2 }}
                    onClick={() => setAlertsExpanded(!alertsExpanded)}
                >
                    <Typography
                        variant="h6"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ color: 'warning.main' }}
                    >
                        <ErrorOutlineIcon /> Alert Notes
                        {field.alertNotes && field.alertNotes.length > 0 && (
                            <Chip
                                label={`${field.alertNotes.length}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                            />
                        )}
                    </Typography>
                    <IconButton size="small">
                        {alertsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={alertsExpanded}>
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: alpha('#ed6c02', 0.05),
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider'
                        }}
                    >
                        <AlertNotesConfig
                            notes={field.alertNotes || []}
                            onChange={handleAlertNotesChange}
                        />
                    </Box>
                </Collapse>
            </Box>

            <Divider sx={{ my: 3 }} />
            <Box>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: 'pointer', mb: 2 }}
                    onClick={() => setConditionalExpanded(!conditionalExpanded)}
                >
                    <Typography
                        variant="h6"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ color: 'info.main' }}
                    >
                        {/* todo change icon */}

                        <ExpandLessIcon /> Conditional Visibility
                        {field.conditions && field.conditions.length > 0 && (
                            <Chip
                                label={`${field.conditions.length} rule(s)`}
                                size="small"
                                color="info"
                                variant="outlined"
                            />
                        )}
                    </Typography>
                    <IconButton size="small">
                        {conditionalExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={conditionalExpanded}>
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: alpha('#0288d1', 0.05),
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider'
                        }}
                    >
                        <ConditionalLogicConfig
                            type="field"
                            conditions={field.conditions || []}
                            onChange={(conditions) => onChange({ ...field, conditions })}
                            currentSectionId={currentSectionId}
                            availableFields={availableFieldsInCurrentSection.filter(
                                f => f.fieldName !== field.name
                            )}
                        />
                    </Box>
                </Collapse>
            </Box>
        </Paper>
    );
};

export default FieldConfig;